/**
 * TWO WAYS TO GET TO A GREEN FLAG — and which of them owns the track.
 *
 * Howard, 17 Aug 2026:
 *
 *   "I think starting a season option ought to be on the race week creator as
 *    well as an option and lead into race #1 no… plus the careers i assume are
 *    seasons and the racetracks are controlled by the season (prescheduled each
 *    year with changes to it) so the user wouldnt pick the track upon career
 *    creation."
 *
 * That is a rule about AUTHORITY, not about layout. There are exactly two ways
 * a race can get a track, and they must never both be live at once:
 *
 *   • EXHIBITION — the player picks. One race, at a track they chose, for its
 *     own sake. Nothing carries in and nothing carries out.
 *   • SEASON — `buildSchedule(seed)` picks. Thirty-six named races in a fixed
 *     order (D-004/D-012), and race N is at the track the calendar says it is
 *     at, for the distance the calendar says it runs. A track select on screen
 *     in this mode is a control that appears to do something it does not.
 *
 * `planEntry` is the whole of that decision and it is pure, so the screen never
 * has to reason about which of its own inputs still apply. It also carries the
 * PROVENANCE — one sentence naming who chose this track — because the whole
 * point of the change is that a player can tell the two modes apart at a
 * glance.
 *
 * WHAT THIS FILE DOES NOT DO, stated here so nothing reads more into it: it
 * does not run a season. It selects race N off the calendar and names who chose
 * it. Carrying feuds, suspensions, TV-list hold-overs, standings, the playoff
 * bracket and the money from one race to the next belongs to
 * `domain/seasonPlay.ts` — `beginSeason` / `runSeasonRace` / `finishSeason`,
 * one race at a time, with the whole carry on a serialisable `SeasonState`.
 * `components/SeasonPlay.tsx` is the screen over that machine.
 *
 * ⚠ DELETED ON PURPOSE, so nobody re-adds it: `SEASON_HANDOFF_LIMIT = 1` and
 * `seasonReachLine()`, which told the player that races 2–36 could not be
 * played one at a time. That was a true measurement of the old `runSeason`
 * `for` loop and it is FALSE now. Both are gone rather than left unrendered,
 * because a constant that measures a limit which no longer exists is a lie
 * waiting to be printed a second time.
 */

import {
  buildSchedule,
  printedSchedule,
  raceAt,
  SEASON_RACES,
  type SeasonSchedule,
  type ScheduledRace,
} from '../domain/schedule.ts';
import { SCENARIO_2025_BLURB, SCENARIO_2025_YEAR } from '../domain/scenario2025.ts';
import { trackCard } from '../domain/turn.ts';

export type EntryMode = 'exhibition' | 'season';

/**
 * A CAREER THAT STARTS SOMEWHERE REAL — D-EZ-76.
 *
 * Howard: *"I want you to load a seed starting scenario for users to be able to
 * use the 2025 season (drivers and tracks) to start their career association."*
 *
 * ⚠ A SCENARIO IS A THIRD THING TO ASK OF THE SEED, NOT A THIRD MODE. Season and
 * exhibition are about who owns the TRACK, which is the distinction this file
 * exists to hold. A scenario does not touch that: the calendar still owns the
 * track, the calendar is simply one that was printed rather than one that was
 * dealt. Making it a mode would have put the track select back on screen for a
 * year whose schedule is more fixed than any other.
 *
 * `null` is the ordinary generated year and stays the default. See
 * `scenario2025.ts` for what the 2025 one actually contains.
 */
export type ScenarioId = typeof SCENARIO_2025_BLURB.id;

export const SCENARIOS = [SCENARIO_2025_BLURB] as const;

/** Howard: *"lead into race #1"*. A season starts at its opener, never mid-year. */
export const SEASON_OPENER = 1;

export interface EntryChoice {
  mode: EntryMode;
  /** In season mode this also builds the calendar: one seed, one year. */
  seed: number;
  /** What the track select is showing. Ignored entirely in season mode. */
  pickedTrackId: string;
  /** Exhibition only — the player may override the card's own distance. */
  useCardLength: boolean;
  customLength: number;
  /** Season only. 1-based; defaults to the opener. */
  raceNumber?: number;
  /** Season only. A printed year to start from, or null for a dealt one. */
  scenario?: ScenarioId | null;
}

export interface EntryPlan {
  mode: EntryMode;
  /** The track this race is at, whoever chose it. */
  trackId: string;
  /** Its length in turns. One turn is ten miles (RB/ii). */
  totalTurns: number;
  /** The event's name — the calendar's in season mode, the card's otherwise. */
  raceName: string;
  /** The calendar entry, in season mode. Null in exhibition: there is no calendar. */
  scheduled: ScheduledRace | null;
  /**
   * `${trackId}#${variant}` — what a race CREST hangs on (`lib/logoStore.ts`).
   *
   * Present in BOTH modes, which is the point of putting it here. In season
   * mode it is the calendar's. In exhibition there is no calendar, but there is
   * still a named race: picking a track card runs that card's HEADLINE race,
   * variant 0 — the same race, with the same name, that the calendar schedules
   * when it draws this card. So the crest is the same crest, and a header that
   * showed one in a season and not in an exhibition would be describing the
   * same race two different ways.
   */
  raceKey: string;
  /**
   * May the track select and the distance control do anything? False in season
   * mode, where the screen must show the schedule's choice as a fact rather
   * than leave a live-looking control that is ignored.
   */
  trackChoiceIsLive: boolean;
  /** One sentence naming who chose this track, for the screen to print. */
  provenance: string;
}

/**
 * Cheap wrapper so a screen can memoise one calendar per seed.
 *
 * With a scenario the calendar is the one that was PRINTED — 36 races in the
 * order they ran — and the seed then means only what it always meant everywhere
 * else: every die of the year.
 */
export function scheduleFor(seed: number, scenario: ScenarioId | null = null): SeasonSchedule {
  if (scenario === SCENARIO_2025_BLURB.id) return printedSchedule(seed, SCENARIO_2025_YEAR);
  return buildSchedule(seed);
}

/**
 * Resolve a screen's controls into the race that will actually be run.
 *
 * `schedule` is passed in when the caller already has one for this seed (a
 * React memo, normally); it is rebuilt here when it is not, so this function is
 * usable — and testable — on its own. A schedule built for a DIFFERENT seed is
 * refused rather than used, because the calendar and the dice have to come from
 * the same number or the race on screen is not the race the season would run.
 */
export function planEntry(choice: EntryChoice, schedule?: SeasonSchedule): EntryPlan {
  if (choice.mode === 'exhibition') {
    const card = trackCard(choice.pickedTrackId);
    return {
      mode: 'exhibition',
      trackId: card.id,
      totalTurns: choice.useCardLength ? card.turns : choice.customLength,
      raceName: card.raceName ?? card.name,
      scheduled: null,
      // Variant 0 — the card's headline race, which is exactly what
      // `raceName` above resolves to. See the note on the field.
      raceKey: `${card.id}#0`,
      trackChoiceIsLive: true,
      // TERSE ON PURPOSE. Howard, 17 Aug 2026: *"what are these instructions,
      // these are terrible? Do not overexplain"*. One clause of who chose, one
      // clause of what it costs. The reasoning lives in comments like this one.
      provenance: 'You picked this track. Nothing carries in or out.',
    };
  }

  if (schedule && schedule.seed !== choice.seed) {
    throw new RangeError(
      `the calendar was built for seed ${schedule.seed} but the race is being set up on seed ${choice.seed}`,
    );
  }
  const calendar = schedule ?? scheduleFor(choice.seed, choice.scenario ?? null);
  const raceNumber = choice.raceNumber ?? SEASON_OPENER;
  const scheduled = raceAt(calendar, raceNumber);

  return {
    mode: 'season',
    trackId: scheduled.trackId,
    // The CALENDAR's distance, not the card's: fourteen cards print a second
    // named race at a different length (N-005), and race 12 of the year is that
    // event, at that distance, not the card's headline race.
    totalTurns: scheduled.turns,
    raceName: scheduled.raceName,
    scheduled,
    // The calendar's own key — which may be a `#1` variant, because fourteen
    // cards print a second named race (N-005) and race 12 of the year may be
    // that one rather than the card's headline.
    raceKey: scheduled.raceKey,
    trackChoiceIsLive: false,
    // A label, not a lecture: the number, the place, the distance.
    provenance: raceLabel(scheduled),
  };
}

/**
 * ONE LINE FOR A SCHEDULED RACE — "Race 1 of 36 · Darlington · 40 turns".
 *
 * Every screen that names a season race uses this, so the entry card, the
 * season panel and the race tape cannot end up describing the same race three
 * different ways.
 */
export function raceLabel(scheduled: ScheduledRace): string {
  return `Race ${scheduled.raceNumber} of ${SEASON_RACES} · ${scheduled.trackName} · ${scheduled.turns} turns`;
}
